import css from "./CompositeCellEditor.sass?inline"
import {DefaultObservableValue, Errors, Lifecycle, MutableObservableValue, Option, panic, Terminable} from "@opendaw/lib-std"
import {createElement} from "@opendaw/lib-jsx"
import {Vertex} from "@opendaw/lib-box"
import {Events, Html} from "@opendaw/lib-dom"
import {Promises} from "@opendaw/lib-runtime"
import {TextScroller} from "@/ui/TextScroller"
import {Surface} from "@/ui/surface/Surface"
import {Colors, IconSymbol, Pointers} from "@opendaw/studio-enums"
import {AudioEffectCompositeCellBoxAdapter, DeviceHost} from "@opendaw/studio-adapters"
import {Icon} from "@/ui/components/Icon"
import {Checkbox} from "@/ui/components/Checkbox"
import {Knob} from "@/ui/components/Knob.tsx"
import {AutomationControl} from "@/ui/components/AutomationControl"
import {RelativeUnitValueDragging} from "@/ui/wrapper/RelativeUnitValueDragging.tsx"
import {SnapCenter, SnapCommonDecibel} from "@/ui/configs.ts"
import {EditWrapper} from "@/ui/wrapper/EditWrapper.ts"
import {TextTooltip} from "@/ui/surface/TextTooltip"
import {MenuButton} from "@/ui/components/MenuButton"
import {ClipboardManager, DevicesClipboard, MenuItem} from "@opendaw/studio-core"
import {MenuItems} from "@/ui/devices/menu-items"
import {DebugMenus} from "@/ui/menu/debug"
import {StudioService} from "@/service/StudioService"

const className = Html.adoptStyleSheet(css, "CompositeCellEditor")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
    host: DeviceHost
}

// Shown in the instrument slot while a composite ENTRY is edited: the way BACK to the parent composite plus the
// entry's own gain / pan / mute / solo, matching the entry row's controls.
export const CompositeCellEditor = ({lifecycle, service, host}: Construct) => {
    const {editing, midiLearning, userEditingManager, deviceSelection} = service.project
    // A composite CELL accepts the Editing pointer at the box level; an AUDIO UNIT only through its `editing`.
    const parent = host.deviceHost()
    const backTarget: Vertex<Pointers> = parent instanceof AudioEffectCompositeCellBoxAdapter
        ? parent.box
        : parent.audioUnitBoxAdapter().box.editing
    const entry = host instanceof AudioEffectCompositeCellBoxAdapter ? host : null
    const muteValue = new DefaultObservableValue(false)
    const soloValue = new DefaultObservableValue(false)
    // The parent composite's name as a NORMAL device-header label (scroller + dblclick rename, no pill):
    // clicking SELECTS it (clearing the device selection), so a following paste lands at the start of this
    // branch's chain — the instrument-header semantics. Going back moves to the button above the numbers.
    const name: HTMLElement = (
        <h1 onInit={element => {
            lifecycle.ownAll(
                TextScroller.install(element),
                Events.subscribeDblDwn(element, async event => {
                    if (entry === null) {return}
                    const labelField = entry.compositeDevice().labelField
                    const {status, error, value} = await Promises.tryCatch(Surface.get(element)
                        .requestFloatingTextInput(event, labelField.getValue()))
                    if (status === "rejected") {
                        if (!Errors.isAbort(error)) {return panic(error)}
                    } else {
                        editing.modify(() => labelField.setValue(value))
                    }
                })
            )
        }}/>
    )
    // The standard device-editor hamburger: the same menu as every input-slot editor. For a one-sided
    // host it offers exactly the audio "Add ..." entries, targeting THIS branch's chain.
    const menu: HTMLElement = (
        <MenuButton root={MenuItem.root().setRuntimeChildrenProcedure(parent => {
            if (entry === null) {
                MenuItems.forAudioUnitInput(parent, service, host)
            } else {
                MenuItems.forCompositeCell(parent, service, host, entry.compositeDevice())
            }
            parent.addMenuItem(DebugMenus.debugBox(entry === null ? host.audioUnitBoxAdapter().box : entry.box))
        })} style={{minWidth: "0", fontSize: "14px", marginLeft: "auto"}}
                    appearance={{color: Colors.shadow, activeColor: Colors.bright}}>
            <Icon symbol={IconSymbol.Menu}/>
        </MenuButton>
    )
    const header: HTMLElement = <h1 className="header" tabIndex={0}>{name}{menu}</h1>
    const backButton: HTMLElement = (
        <div className="back-button">
            <Icon symbol={IconSymbol.RoundUp}/>
        </div>
    )
    const controls = entry === null ? <div/> : (
        <div className="controls">
            <div className="channel-mix">
                <AutomationControl lifecycle={lifecycle} editing={editing} midiLearning={midiLearning}
                                   tracks={entry.audioUnitBoxAdapter().tracks} parameter={entry.namedParameter.gain} offset={2}>
                    <RelativeUnitValueDragging lifecycle={lifecycle} editing={editing}
                                               parameter={entry.namedParameter.gain} options={SnapCommonDecibel}>
                        <Knob lifecycle={lifecycle} value={entry.namedParameter.gain} anchor={0.0} color={Colors.yellow}/>
                    </RelativeUnitValueDragging>
                </AutomationControl>
                <AutomationControl lifecycle={lifecycle} editing={editing} midiLearning={midiLearning}
                                   tracks={entry.audioUnitBoxAdapter().tracks} parameter={entry.namedParameter.pan} offset={2}>
                    <RelativeUnitValueDragging lifecycle={lifecycle} editing={editing}
                                               parameter={entry.namedParameter.pan} options={SnapCenter}>
                        <Knob lifecycle={lifecycle} value={entry.namedParameter.pan} anchor={0.5} color={Colors.green}/>
                    </RelativeUnitValueDragging>
                </AutomationControl>
            </div>
            <div className="channel-isolation">
                <Checkbox lifecycle={lifecycle} model={muteValue}
                          appearance={{activeColor: Colors.orange, framed: true, tooltip: "Mute entry"}}>
                    <Icon symbol={IconSymbol.Mute}/>
                </Checkbox>
                <Checkbox lifecycle={lifecycle} model={soloValue}
                          appearance={{activeColor: Colors.yellow, framed: true, tooltip: "Solo entry"}}>
                    <Icon symbol={IconSymbol.Solo}/>
                </Checkbox>
            </div>
        </div>
    )
    // Every sibling branch as a clickable number badge, the edited one highlighted: quick navigation
    // between the composite's chains without going back to the parent.
    const numbers: HTMLElement = <div className="entry-numbers"/>
    const navigation: HTMLElement = (
        <div className="navigation">
            {backButton}
            {numbers}
        </div>
    )
    const element: HTMLElement = <div className={className}>{header}{controls}{navigation}</div>
    lifecycle.ownAll(
        TextTooltip.default(backButton, () => "Back to the parent chain"),
        Events.subscribe(backButton, "click", () => userEditingManager.audioUnit.edit(backTarget)),
        Events.subscribe(name, "pointerdown", () => {
            deviceSelection.deselectAll()
            header.classList.add("selected")
            header.focus()
        }),
        deviceSelection.catchupAndSubscribe({
            onSelected: () => header.classList.remove("selected"),
            onDeselected: () => {}
        }),
        // The focused header receives the clipboard, exactly like a device header: with the selection
        // cleared by the click above, a paste lands at the start of this branch's chain.
        ClipboardManager.install(header, DevicesClipboard.createHandler({
            getEnabled: () => true,
            editing,
            selection: deviceSelection,
            boxGraph: service.project.boxGraph,
            boxAdapters: service.project.boxAdapters,
            getHost: (): Option<DeviceHost> => Option.wrap(host)
        }))
    )
    if (entry !== null) {
        const composite = entry.compositeDevice()
        const rebuildNumbers = () => {
            Html.empty(numbers)
            composite.entries.adapters().forEach(sibling => numbers.appendChild((
                <div className={Html.buildClassList("entry-number", sibling === entry && "current")}
                     onclick={() => {
                         if (sibling !== entry) {userEditingManager.audioUnit.edit(sibling.box)}
                     }}>{String(sibling.indexField.getValue() + 1)}</div>
            )))
        }
        rebuildNumbers()
        lifecycle.ownAll(
            composite.entries.subscribe({
                onAdd: rebuildNumbers, onRemove: rebuildNumbers, onReorder: rebuildNumbers
            }),
            composite.labelField.catchupAndSubscribe(owner => name.textContent = owner.getValue()),
            connectBoolean(muteValue, EditWrapper.forAutomatableParameter(editing, entry.namedParameter.mute)),
            connectBoolean(soloValue, EditWrapper.forAutomatableParameter(editing, entry.namedParameter.solo))
        )
    } else {
        name.textContent = host.label
    }
    return element
}

// Two-way bind a checkbox model to its parameter wrapper (mirrors the Playfield slot's own helper).
const connectBoolean = (value: MutableObservableValue<boolean>,
                        wrapper: MutableObservableValue<boolean>): Terminable => {
    value.setValue(wrapper.getValue())
    return Terminable.many(
        value.subscribe(owner => wrapper.setValue(owner.getValue())),
        wrapper.subscribe(owner => value.setValue(owner.getValue()))
    )
}
